import React, { useEffect, useState, useCallback } from "react";
import TopNav from "../../Components/Dashboards/TopNav";
import Notifications from "../../Components/Dashboards/Notifications";
import NavHeader2 from "../../Components/Dashboards/NavHeader2";
import Sidebar from "../../Components/Dashboards/Sidebar";
import Footer from "../../Components/Dashboards/Footer";
import BreadcrumTop from "../../Components/Dashboards/BreadcrumTop";
import Nulls from "../../Components/Dashboards/Nulls";
import Spinner from "../../Spinner/Spinner";
import DataTable from "react-data-table-component";
import "../../Components/Dashboards/../../libs/css/dataTable.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { API_ROUTES } from "../../utils/constants";
import { getToken } from "../../utils/getToken";
import { apiFetch } from "../../utils/apiFetch";

export default function ManageChannels(props) {
  const { isLoaded } = props;
  const navigate = useNavigate();

  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loginUserRole, setLoginUserRole] = useState("");
  const [filterText, setFilterText] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [formName, setFormName] = useState("");
  const [formValue, setFormValue] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Manage Channels | Dashboard";
  }, []);

  // Check user role
  useEffect(() => {
    const storedUserData = localStorage.getItem("userVASData");
    if (storedUserData) {
      try {
        const parsedData = JSON.parse(storedUserData);
        const userRoles = parsedData.responseDetails?.roles;
        if (userRoles && userRoles.length > 0) {
          setLoginUserRole(userRoles[0]);
        }
      } catch (error) {
        console.error("Error parsing storedUserData:", error);
      }
    }
  }, []);

  const getAuthHeaders = () => {
    const token = getToken();
    if (!token) {
      Swal.fire({ title: "Session Expired", text: "Please log in again.", icon: "error" }).then(
        () => {
          localStorage.removeItem("userVASData");
          localStorage.removeItem("companyName");
          navigate("/login");
        }
      );
      return null;
    }
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  };

  const fetchChannels = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    setIsLoading(true);
    try {
      const response = await apiFetch(API_ROUTES.GET_ALL_CHANNELS, {
        method: "GET",
        headers,
      });

      if (!response.ok) throw new Error("Failed to fetch channels.");

      const json = await response.json();
      setChannels(json.data || []);
    } catch (error) {
      console.error("Error fetching channels:", error);
      Swal.fire({ title: "Error", text: "Could not load channels.", icon: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // ---- Form helpers ----
  const openCreateModal = () => {
    setModalMode("create");
    setSelectedChannel(null);
    setFormName("");
    setFormValue("");
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (channel) => {
    setModalMode("edit");
    setSelectedChannel(channel);
    setFormName(channel.name);
    setFormValue(channel.value);
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formName.trim()) errors.name = "Display name is required.";
    if (!formValue.trim()) errors.value = "Channel value is required.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const headers = getAuthHeaders();
    if (!headers) return;

    setSubmitting(true);
    try {
      const isEdit = modalMode === "edit";
      const url = isEdit
        ? `${API_ROUTES.UPDATE_CHANNEL}/${selectedChannel.id}`
        : API_ROUTES.CREATE_CHANNEL;

      const response = await apiFetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers,
        body: JSON.stringify({ name: formName.trim(), value: formValue.trim() }),
      });

      let json = {};
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        json = await response.json();
      }

      if (response.status === 409) {
        setFormErrors({ value: json.message || "A channel with that value already exists." });
        return;
      }

      if (!response.ok) throw new Error(json.message || "An error occurred.");

      Swal.fire({
        title: "Success",
        text: isEdit ? "Channel updated successfully." : "Channel created successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      closeModal();
      fetchChannels();
    } catch (error) {
      Swal.fire({ title: "Error", text: error.message || "Something went wrong.", icon: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (channel) => {
    const result = await Swal.fire({
      title: "Delete Channel?",
      html: `Are you sure you want to delete <strong>${channel.name}</strong>? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const response = await apiFetch(`${API_ROUTES.DELETE_CHANNEL}/${channel.id}`, {
        method: "DELETE",
        headers,
      });

      const json = await response.json();

      if (!response.ok) throw new Error(json.message || "Failed to delete channel.");

      Swal.fire({ title: "Deleted!", text: "Channel deleted.", icon: "success", timer: 2000, showConfirmButton: false });
      fetchChannels();
    } catch (error) {
      Swal.fire({ title: "Error", text: error.message || "Something went wrong.", icon: "error" });
    }
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => index + 1,
      width: "60px",
    },
    {
      name: "Display Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Value (used in filter)",
      selector: (row) => row.value,
      sortable: true,
      cell: (row) => (
        <span className="badge bg-secondary" style={{ fontSize: "0.85rem", padding: "6px 10px" }}>
          {row.value}
        </span>
      ),
    },
    {
      name: "Created",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleDateString("en-GB") : "—",
    },
  ];

  const ExpandedComponent = ({ data }) => (
    <div className="text-left th_data mt-3 pb-4">
      {loginUserRole === "Admin" ? (
        <div>
          <font>Action: &nbsp;&nbsp; </font>
          <span style={{ textTransform: "uppercase" }}>
            <button
              className="mx-2"
              style={{
                fontSize: "13px",
                textTransform: "none",
                backgroundColor: "#6a73fa",
                border: "none",
                color: "#fff",
                padding: "8px 10px",
                borderRadius: "6px",
              }}
              onClick={() => openEditModal(data)}
            >
              Edit Channel
            </button>
            <button
              className="mx-1"
              style={{
                fontSize: "13px",
                textTransform: "none",
                backgroundColor: "red",
                border: "none",
                color: "white",
                padding: "8px 10px",
                borderRadius: "6px",
              }}
              onClick={() => handleDelete(data)}
            >
              Delete Channel
            </button>
          </span>
        </div>
      ) : (
        <div className="text-muted" style={{ fontSize: "13px" }}>
          No actions available.
        </div>
      )}
    </div>
  );

  const filteredData = channels.filter((channel) =>
    channel.name?.toLowerCase().includes(filterText.toLowerCase()) ||
    channel.value?.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <>
      <Nulls />
      <div id="main-wrapper" className={isLoaded ? "show" : ""}>
        <TopNav />
        <Notifications />
        <NavHeader2 />
        <Sidebar />
        <div className="content-body mt--10">
          <div className="container-fluid pl-xs-0 pr-xs-0 pt-xs-0">
            <BreadcrumTop title="Manage Channels" />

            <div className="row">
              <div className="container d-flex justify-content-end align-items-center mb-4">
                <div className="col-md-5 col-sm-12 mx-1 d-flex justify-content-end">
                  <input
                    type="text"
                    placeholder="Search by name or value"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="form-control"
                  />
                  {loginUserRole === "Admin" && (
                    <div className="col-md-5 col-sm-12 mx-1">
                      <button className="btn btn-primary" onClick={openCreateModal}>
                        Add Channel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              {isLoading ? (
                <Spinner loading={isLoading} />
              ) : (
                <DataTable
                  className="services_datatable display text-nowrap"
                  title=""
                  expandableRows
                  expandableRowsComponent={ExpandedComponent}
                  columns={columns}
                  data={filteredData}
                  highlightOnHover
                  pagination
                  paginationPerPage={20}
                  paginationRowsPerPageOptions={[10, 20, 30, 50]}
                  noDataComponent={
                    <div className="py-4 text-muted">
                      No channels found. Click <strong>Add Channel</strong> to create one.
                    </div>
                  }
                />
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalMode === "create" ? "Add New Channel" : "Edit Channel"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  disabled={submitting}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Display Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.name ? "is-invalid" : ""}`}
                      placeholder="e.g. SecureD"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      disabled={submitting}
                    />
                    {formErrors.name && (
                      <div className="invalid-feedback">{formErrors.name}</div>
                    )}
                    <small className="text-muted">
                      This is what users see in the dropdown.
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Channel Value <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.value ? "is-invalid" : ""}`}
                      placeholder="e.g. SecureD"
                      value={formValue}
                      onChange={(e) => setFormValue(e.target.value)}
                      disabled={submitting}
                    />
                    {formErrors.value && (
                      <div className="invalid-feedback">{formErrors.value}</div>
                    )}
                    <small className="text-muted">
                      This is the actual value sent to the API when filtering data.
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving...
                      </>
                    ) : modalMode === "create" ? (
                      "Add Channel"
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
